import styled from '@emotion/styled'
import {
  UpdateUserApprovalBodyDto,
  UpdateUserInformationBodyDto,
} from '@pharmac-extranet/extranet-api'
import { useBreakpoint, useLanguage } from '@pharmac-extranet/shared'
import {
  PharmacColumn,
  PharmacContainer,
  PharmacLoadingDots,
  PharmacSuccessToast,
  PharmacTab,
  PharmacTabs,
  usePharmacToast,
} from '@pharmac/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { UpdateUserInfoForm } from '../../components/user/UpdateUserInfoForm'
import { UserInfoForm } from '../../components/user/UserInfoForm'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCurrentUser } from '../../features/users/useCurrentUser'
import { extranetUsersQueryKeyFactory } from '../../features/users/users.queries'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'

const InfoFormContainer = styled(PharmacContainer)`
  background-color: var(--color-accent-2);
  border-radius: 0 20px 20px 20px;
`

export const UserView = () => {
  const { data, isLoading } = useCurrentUser()
  const { t } = useLanguage(['extranet', 'common'])

  const [edit, setEdit] = useState<boolean>(false)

  const navigate = useNavigate()
  const createLink = useExtranetPath()

  const queryClient = useQueryClient()
  const toastHook = usePharmacToast()

  const { user } = useAuthenticationContext()
  const { maxMobile, minDesktop } = useBreakpoint()

  const approvalFormHook = useForm<UpdateUserApprovalBodyDto>({
    mode: 'onChange',
    defaultValues: {
      approval: false,
    },
  })

  const userFormHook = useForm<UpdateUserInformationBodyDto>({
    mode: 'onChange',
    defaultValues: {
      countryCode: '',
      emailAddress: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      postCode: '',
      streetName: '',
      townName: '',
      certificateCardNumber: '',
      professionalRoleIds: [],
    },
  })

  useEffect(() => {
    if (data) {
      userFormHook.reset({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        emailAddress: data.emailAddress || '',
        countryCode: data.countryCode || '',
        phoneNumber: data.phoneNumber || '',
        postCode: data.postCode || '',
        streetName: data.streetName || '',
        townName: data.townName || '',
        certificateCardNumber: data.certificateCardNumber || '',
        professionalRoleIds: (data.professionalRoles || []).map((role) => role.id),
      })
    }
  }, [data])

  const { mutate: updateUserInfo, isPending: isUpdateUserInfoPending } = useMutation({
    mutationFn: (dto: UpdateUserInformationBodyDto) => api.users.updateUserInformation(dto),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('user.infoUpdatedSuccessfully', { ns: 'extranet' })}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: extranetUsersQueryKeyFactory.getCurrentUser(),
      })
      setEdit(false)
      navigate(createLink('user'))
    },
  })

  const handleUpdateUserInfo = (dto: UpdateUserInformationBodyDto) => {
    updateUserInfo(dto)
  }

  const { mutate: updateMarketing } = useMutation({
    mutationFn: (dto: UpdateUserApprovalBodyDto) => api.users.updateUserMarketingApproval(dto),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('user.approvalChangedSuccessfully', { ns: 'extranet' })}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: extranetUsersQueryKeyFactory.getCurrentUser(),
      })
    },
  })

  const { mutate: updateCookie } = useMutation({
    mutationFn: (dto: UpdateUserApprovalBodyDto) => api.users.updateUserCookieApproval(dto),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('extranet:user.approvalChangedSuccessfully')}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: extranetUsersQueryKeyFactory.getCurrentUser(),
      })
    },
  })

  const { mutate: updatePrivacy } = useMutation({
    mutationFn: (dto: UpdateUserApprovalBodyDto) => api.users.updateUserPrivacyApproval(dto),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('user.approvalChangedSuccessfully', { ns: 'extranet' })}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: extranetUsersQueryKeyFactory.getCurrentUser(),
      })
    },
  })

  const { mutate: updateTOS } = useMutation({
    mutationFn: (dto: UpdateUserApprovalBodyDto) => api.users.updateUserTermsOfServiceApproval(dto),
    onSuccess: async () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: (
          <PharmacSuccessToast>
            {t('user.approvalChangedSuccessfully', { ns: 'extranet' })}
          </PharmacSuccessToast>
        ),
      })

      queryClient.invalidateQueries({
        queryKey: extranetUsersQueryKeyFactory.getCurrentUser(),
      })
    },
  })

  const handleUpdateMarketing = (value: boolean) => {
    updateMarketing({ approval: value })
  }

  const handleUpdateCookie = (value: boolean) => {
    updateCookie({ approval: value })
  }

  const handleUpdatePrivacy = (value: boolean) => {
    updatePrivacy({ approval: value })
  }

  const handleUpdateTOS = (value: boolean) => {
    updateTOS({ approval: value })
  }

  if (isLoading || !data) {
    return (
      <ExtranetWrapper>
        <PharmacLoadingDots />
      </ExtranetWrapper>
    )
  }

  if (!user) return null

  return (
    <ExtranetWrapper>
      <PharmacColumn
        paddingLargeTop
        paddingLarge={!minDesktop}
        paddingMedium={maxMobile}
      >
        <PharmacTabs singleTab>
          {[
            <PharmacTab handle={t('extranet:user.title')}>
              <InfoFormContainer
                paddingMedium
                paddingSmallHorizontal={maxMobile}
              >
                <>
                  {edit ? (
                    <FormProvider {...userFormHook}>
                      <UpdateUserInfoForm
                        saveDisabled={
                          !userFormHook.formState.isValid ||
                          !userFormHook.formState.isDirty ||
                          isUpdateUserInfoPending
                        }
                        saveButtonClick={userFormHook.handleSubmit(handleUpdateUserInfo)}
                        cancelButtonClick={() => {
                          userFormHook.reset()
                          setEdit(false)
                          navigate(createLink('user'))
                        }}
                      />
                    </FormProvider>
                  ) : (
                    <FormProvider {...approvalFormHook}>
                      <UserInfoForm
                        userInfo={user}
                        marketingOnChange={handleUpdateMarketing}
                        cookieOnChange={handleUpdateCookie}
                        privacyOnChange={handleUpdatePrivacy}
                        tosOnChange={handleUpdateTOS}
                        editButtonClick={() => {
                          setEdit(true)
                          navigate(createLink('user.manage'))
                        }}
                      />
                    </FormProvider>
                  )}
                </>
              </InfoFormContainer>
            </PharmacTab>,
          ]}
        </PharmacTabs>
      </PharmacColumn>
    </ExtranetWrapper>
  )
}
